/**
 * `@vc-frontend/core` — the curated PUBLIC surface a Module Federation plugin may
 * import from the host. A facade: re-exports host code, nothing moves.
 * Publish-from-source: plugins consume TYPES only (compiled contract/index.d.ts); the
 * host provides the live instance at runtime via the MF shared singleton.
 * Keep it SMALL and additive — removing/renaming an export breaks every plugin.
 */

/**
 * Registers every `Vc*` component globally: `app.use(uiKit)`. Needed by any app instance that
 * renders `OrderStatus`, which resolves `VcChip` / `VcIcon` / `VcTooltip` globally.
 *
 * Inert until the package grows a runtime entry: the root export is types-only, so
 * `@vc-frontend/core` does not resolve at runtime outside the MF shared scope, and inside the
 * host `app.use(uiKit)` has already run at boot.
 */
export { uiKit } from "@/ui-kit";

// Curated components for explicit imports, so a plugin can name what it uses.
export { VcWidget } from "@/ui-kit/components";
export {
  VcAlert,
  VcButton,
  VcCalendar,
  VcChip,
  VcDialog,
  VcDialogContent,
  VcDialogFooter,
  VcDialogHeader,
  VcEmptyView,
  VcInput,
  VcLoaderOverlay,
  VcMenuItem,
  VcPopover,
  VcRating,
  VcSelect,
  VcTabSwitch,
  VcTextarea,
  VcTypography,
} from "@/ui-kit/components/molecules";
// `VcImage` needs the host too: its thumbnail logic reads the theme's `image_thumbnails_*`
// settings, and `useThemeContext`'s getter THROWS ("Theme context is missing.") until the host has
// set the context at boot. Only a filename-only `src` returns before that read — any URL `src`
// throws outside the host, so a plugin's own dev server or specs must stub it.
export {
  VcBadge,
  VcBreadcrumbs,
  VcCheckbox,
  VcCheckboxGroup,
  VcIcon,
  VcImage,
  VcInputDetails,
  VcLabel,
  VcLink,
  VcMarkdownRender,
  VcTooltip,
} from "@/ui-kit/components/atoms";
export {
  VcDatePicker,
  VcDateRangePicker,
  VcModal,
  VcPagination,
  VcTable,
  VcTableColumn,
  VcWidgetSkeleton,
} from "@/ui-kit/components/organisms";
export { VcLayout } from "@/ui-kit/components/templates";

/**
 * Themed order-status chip. Its status -> colour/icon mapping comes from the THEME's
 * `orders_statuses` (config/settings_data.json, reached as `themeContext.settings`) — not from
 * the store settings the backend returns, which land on `themeContext.storeSettings`. There is
 * no query that serves it, so a plugin rendering its own chip would drift from the host on a
 * theme it cannot read. Props are contract: renaming or removing one is a breaking change.
 *
 * Renders only inside the host: `useThemeContext`'s getter throws "Theme context is missing."
 * until `setThemeContext` has run, which happens at boot in `app-runner.ts`. That is also why
 * it cannot be rendered from a plugin's own process — a remote always runs inside the host,
 * and the root export is types-only outside the MF shared scope.
 */
export { default as OrderStatus } from "@/shared/account/components/order-status.vue";

// Extension registry — backed by @vueuse createGlobalState (hence @vueuse is shared).
export { useExtensionRegistry } from "@/shared/common/composables/extensionRegistry/useExtensionRegistry";
// The registration keys the host matches against.
export { EXTENSION_CATEGORIES, EXTENSION_NAMES } from "@/shared/common/constants/extensionPointsNames";
// Declared contributions (`@vc-frontend/core/manifest`).
export type { SlotContextMapType, SlotIdType } from "@/shared/common/types/slotContext";
export { usePluginsStatus } from "@/core/federation/contributions/status";
export type { IPluginStatusType, PluginStateType } from "@/core/federation/contributions/status";
export type {
  ComparableConditionType,
  ConditionNodeType,
  ConditionScalarType,
  ConditionType,
  FieldBuilderType,
  GlobalConditionType,
  HostRouteNameType,
  IAccountMenuContributionType,
  IHeaderMenuContributionType,
  IMenuLinkContributionType,
  IPluginContributionsType,
  IPluginManifestConfigType,
  IRouteContributionType,
  ISlotContributionType,
  MenuContributionType,
  MenuDeclarationType,
  MenuLinkDeclarationType,
  RouteDeclarationType,
  SlotConditionType,
  SlotDeclarationType,
  SlotPolicyType,
} from "@/core/federation/contributions/types";

// GraphQL / Apollo — same client, cache and auth link as the host.
export { apolloClient, graphqlClient } from "@/core/api/graphql/client";
export { registerCacheTypePolicies } from "@/core/api/graphql/config/registerCacheTypePolicies";
// Opts one operation out of the host's global error toast, for a read that names its own failure inline.
export { SUPPRESS_ERROR_NOTIFICATIONS_CONTEXT } from "@/core/api/graphql/consts";

// Composables. Subpath imports, not barrels — a barrel re-exports the whole module
// graph and turns the contract's type graph (and Rollup's chunk graph) circular.
export { useModuleSettings } from "@/core/composables/useModuleSettings";
export { useNavigations } from "@/core/composables/useNavigations";
export { useBreadcrumbs } from "@/core/composables/useBreadcrumbs";
export { usePageHead } from "@/core/composables/usePageHead";
// A query-string parameter as a writable ref, so a plugin's selection survives a reload and
// back/forward. Each write pushes a history entry; `updateMethod: "replace"` overwrites it instead.
export { useRouteQueryParam } from "@/core/composables/useRouteQueryParam";
export { useUser } from "@/shared/account/composables/useUser";
export { useModal } from "@/shared/modal/composables/useModal";
export { useNotifications } from "@/shared/notification/composables/useNotifications";
export { useWishlistSharingScopes } from "@/shared/wishlists/composables/useWishlistSharingScopes";
// What the sharing modal hands a scope's `onSaved`, and the shape a scope's element must expose
// for the modal to read it.
export type {
  IWishlistSharingScopeControlsType,
  IWishlistSharingScopeExposeType,
  WishlistSharingScopePayloadType,
  WishlistSharingScopeSavedContextType,
} from "@/shared/wishlists/composables/useWishlistSharingScopes";
export { useThemeContext } from "@/core/composables/useThemeContext";
export { useAnalytics } from "@/core/composables/useAnalytics";
export { useErrorsTranslator } from "@/core/composables/useErrorsTranslator";
export { useAuth } from "@/core/composables/useAuth";
export { useSignMeIn } from "@/shared/account/composables/useSignMeIn";
export { default as IdentityProvider } from "@/shared/sign-in/components/identity-provider.vue";
export type { SignInMethodContributionType } from "@/shared/account/types";
export type { IdentityErrorType, SignInKeyType } from "@/core/api/graphql/types";

export { Logger } from "@/core/utilities";
export { getProductRoute } from "@/core/utilities/product";
// Whether a menu link is the current page's: the link's own `activeWhen` rule, else vue-router's record match. A
// plugin that draws its own account-menu link (an `accountMenu` replacement) highlights it the way the host's rail does.
export { isMenuLinkActive } from "@/core/utilities/menu";
// A date-only bound ("YYYY-MM-DD") to the instant a filter needs: local midnight, or the last
// millisecond of that day. A plugin filtering by date has to land on the host's boundaries, not its own.
export { toEndDateFilterValue, toLocalDateOnly, toStartDateFilterValue } from "@/core/utilities/date";
// A plugin's messages must re-merge on every locale switch, not just at init: the host re-runs
// every registered loader when the language changes. Merge the way the host does — read the
// existing messages and `setLocaleMessage(locale, merge({}, existing, yours))`; vue-i18n's
// `mergeLocaleMessage` mutates the stored object in place.
export { registerLocaleLoader } from "@/core/locale-loaders";
export type { LocaleLoaderType } from "@/core/locale-loaders";

// Host route names a plugin mounts its own routes under (`router.addRoute(ROUTES.COMPANY.NAME, …)`)
// or links to. Vue Router throws on an unknown parent, so these names are contract: without this
// export a plugin hard-codes "Company"/"Account" and a host rename breaks it with every gate green.
// Exporting the whole map is deliberate — it puts every route name a plugin could reach for under
// the contract, so a rename shows up as a contract diff (a minor on 0.x) instead of a silent break.
export { ROUTES } from "@/router/routes/constants";
// The query-string names host pages read (`q` is the search phrase), for a plugin linking to a host page with a query.
// Contract for the same reason as `ROUTES`: a rename shows up as a contract diff instead of a link that stops working.
export { QueryParamName } from "@/core/enums/query-param-name.enum";

// Order rendering, so a plugin's order page shows an order the way the host's own does.
// `useOrderView` is the host's order view-model (gift vs regular items, per-currency groups,
// BOPIS); the components are the ones the host's order details page composes. Direct .vue paths,
// not the shared/* barrels - same reason as the composables above.
export { useOrderView } from "@/shared/account/composables/useOrderView";
export { default as OrderLineItems } from "@/shared/account/components/order-line-items.vue";
export { default as AcceptedGifts } from "@/shared/checkout/components/accepted-gifts.vue";
export { default as OrderCommentSection } from "@/shared/checkout/components/order-comment-section.vue";
export { default as OrderSummary } from "@/shared/checkout/components/order-summary.vue";
export { default as AddressInfo } from "@/shared/common/components/address-info.vue";
export { default as VendorName } from "@/shared/common/components/vendor-name.vue";
// The orders `filter` expression the host builds from its filter panel model, and the facet name
// the status chips read - a plugin listing orders has to speak the same filter grammar. Named for
// orders here: `getFilterExpression` is the host's generic filter joiner in @/core/utilities.
export { getFilterExpression as getOrdersFilterExpression } from "@/shared/account/composables/useUserOrdersFilter";
export type { OrdersFilterDataType } from "@/shared/account/types";
export { STATUS_ORDERS_FACET_NAME } from "@/core/constants/orders";

// Files. `useFetch` is the host's fetch with its auth interceptors, so a plugin can read a
// protected file URL as the signed-in user; `downloadFile` saves one through it.
export { useFetch } from "@/core/api/common/composables/useFetch";
export { downloadFile } from "@/shared/files/utils";
export { getFileSize } from "@/ui-kit/utilities/file-size";
// The date fields' own display format, so a plugin echoing a picked date shows it the way the field did.
export { formatDateLocale, tryParseDate } from "@/ui-kit/utilities/date";
// The host's breakpoint scale, for a plugin that switches layout in script (`useBreakpoints(BREAKPOINTS)`).
export { BREAKPOINTS } from "@/ui-kit/constants/tailwind";
export { ContentType } from "@/core/enums/content-type.enum";

export { globals } from "@/core/globals";
export type { I18n } from "@/i18n";
export type { ILanguage } from "@/core/types";
export type { ExtendedMenuLinkType, MenuType } from "@/core/types";
// What a plugin template reads as `$cfg` / `$context` / `$permissions`: the host sets them as global
// properties of the app instance the plugin renders in.
export type { IThemeConfigSettings, IThemeContext } from "@/core/types";
export { PlatformPermissions, XApiPermissions } from "@/core/enums";
// Already load-bearing in the contract: useOrderView, OrderLineItems and OrderSummary all
// take one, and rollup names it only internally — so a plugin could pass an order but not
// declare the variable it passed.
export type { CustomerOrderType } from "@/core/api/graphql/types";
// Already reachable through the list-sharing types; exported so a plugin can name it.
export type { SharingTargetType } from "@/core/api/graphql/types";

import { version } from "./package.json";
/** Contract version, single-sourced from core-api/package.json (managed by build:core-types / bump:core). */
export const CORE_VERSION: string = version;
